import styled from "@emotion/styled";
import { DateTime } from "luxon";
import { Text } from "styles/utility-components";

import { currencyFormat } from "components/app_layout/Typography/Numbers";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import TooltipComponent from "components/ui/tooltip/Tooltip";
import { useBuyerSupplierAwardStats } from "lib/hooks/api/buyer/useBuyerSupplierAwardStats";
import { supplierSpendFilter } from "lib/hooks/api/spend/spendFilters";
import { useSpendTransactions } from "lib/hooks/api/spend/useSpendTransactions";
import { useTopSpendSuppliers } from "lib/hooks/api/spend/useTopSpendSuppliers";
import { UIcon } from "lib/icons/untitled_ui/UIcon";
import { LAPTOP_SM_MIN_PX, TABLET_MIN_PX } from "lib/providers/Responsive";
import { SupplierMentionType } from "lib/types/graphQLEnums";
import { formatDate, pluralise } from "lib/utils";
import { toHumanRelativeDateFromNow } from "lib/utils/relative_time";

type Props = {
  buyerGuid: string;
  supplierGuid: string;
  hasSpendAccess: boolean;
  /** Which notices count, so the figures agree with the chart and table below them. */
  supplierMentionType: SupplierMentionType;
  /** Start of the page's date range as YYYY-MM-DD, or undefined for all time. */
  dateFrom?: string;
};

const AWARD_VALUE_HINT =
  "The headline value of published awards. An award naming several suppliers is split evenly between them; awards that only mention the supplier count at full value. Often a multi-year upper estimate, so it isn't comparable with what has been invoiced.";

/**
 * The relationship's totals, each with a count and its most recent activity, so a reader can
 * tell a live relationship from one that stopped years ago without reading the chart.
 */
export function BuyerSupplierHeadlineFigures({
  buyerGuid,
  supplierGuid,
  hasSpendAccess,
  supplierMentionType,
  dateFrom,
}: Props) {
  return (
    <Figures>
      {hasSpendAccess && (
        <SpendFigure buyerGuid={buyerGuid} supplierGuid={supplierGuid} dateFrom={dateFrom} />
      )}
      <AwardFigure
        buyerGuid={buyerGuid}
        supplierGuid={supplierGuid}
        supplierMentionType={supplierMentionType}
        dateFrom={dateFrom}
      />
    </Figures>
  );
}

/**
 * Whether the figures are still loading. The page holds its placeholder until they are in;
 * it asks the same queries as the figures, so nothing is fetched twice.
 */
export function useHeadlineFiguresLoading({
  buyerGuid,
  supplierGuid,
  hasSpendAccess,
  supplierMentionType,
  dateFrom,
}: Props): boolean {
  const spend = useSpendFigure({ buyerGuid, supplierGuid, dateFrom }, hasSpendAccess);
  const awards = useAwardFigure({ buyerGuid, supplierGuid, supplierMentionType, dateFrom });
  return (hasSpendAccess && spend.isLoading) || awards.isLoading;
}

type SpendFigureProps = Pick<Props, "buyerGuid" | "supplierGuid" | "dateFrom">;

function useSpendFigure({ buyerGuid, supplierGuid, dateFrom }: SpendFigureProps, enabled = true) {
  const filters = { ...supplierSpendFilter({ buyerGuid, supplierGuid }), dateFrom };
  // The total and the count come from the same row, so they always describe the same spend.
  const total = useTopSpendSuppliers({ filters, limit: 1 }, { enabled });
  // Transactions come back newest first, so the first row is the latest invoice.
  const latest = useSpendTransactions({ filters, limit: 1 }, { enabled });
  return { total, latest, isLoading: total.isLoading || latest.isLoading };
}

type AwardFigureProps = Omit<Props, "hasSpendAccess">;

function useAwardFigure({
  buyerGuid,
  supplierGuid,
  supplierMentionType,
  dateFrom,
}: AwardFigureProps) {
  return useBuyerSupplierAwardStats(buyerGuid, supplierGuid, supplierMentionType, dateFrom);
}

function SpendFigure(props: SpendFigureProps) {
  const { dateFrom } = props;
  const { total, latest, isLoading } = useSpendFigure(props);

  const supplierTotal = total.data?.results[0];
  const transactionCount = supplierTotal?.transactionCount ?? 0;

  return (
    <HeadlineFigure
      label="Invoiced spend"
      isLoading={isLoading}
      isError={total.isError || latest.isError}
      emptyText={transactionCount === 0 ? emptyMessage("No invoiced spend", dateFrom) : undefined}
      total={supplierTotal?.totalValue ?? null}
      count={pluralise(transactionCount, "transaction", true)}
      latestLabel="last invoice"
      latestDate={latest.data?.results[0]?.date}
    />
  );
}

// The total is only ever awards naming the supplier, whatever the mention type: a notice that
// mentions them isn't money they won, and nor is its date the last award. The count follows the
// page's notice set.
function AwardFigure(props: AwardFigureProps) {
  const { dateFrom } = props;
  const { data, isLoading, isError } = useAwardFigure(props);

  const noticeCount = data?.noticeCount ?? 0;

  return (
    <HeadlineFigure
      label="Award value"
      hint={AWARD_VALUE_HINT}
      isLoading={isLoading}
      isError={isError}
      emptyText={noticeCount === 0 ? emptyMessage("No notices", dateFrom) : undefined}
      total={data?.awardValue ?? null}
      emptyTotalText="Value not published"
      count={pluralise(noticeCount, "notice", true)}
      latestLabel="last award"
      latestDate={data?.latestAwardDate}
    />
  );
}

type HeadlineFigureProps = {
  label: string;
  /** Explains the figure; shown from an info icon beside the label. */
  hint?: string;
  isLoading: boolean;
  isError: boolean;
  /** Set when there is nothing to report, in place of the count and date. */
  emptyText?: string;
  total: number | null;
  /** Leads the detail line when there is activity but no total to show. */
  emptyTotalText?: string;
  count: string;
  latestLabel: string;
  latestDate: string | null | undefined;
};

function HeadlineFigure({
  label,
  hint,
  isLoading,
  isError,
  emptyText,
  total,
  emptyTotalText,
  count,
  latestLabel,
  latestDate,
}: HeadlineFigureProps) {
  const hasTotal = !isError && !emptyText && total !== null;

  return (
    <Figure aria-busy={isLoading}>
      <Headline>
        {isLoading ? (
          <SkeletonText width="96px" fontSize={24} shimmer />
        ) : (
          // With no figure, a quiet placeholder dash: the line underneath says why.
          <Text h1 as="p" color={hasTotal ? undefined : "sysTextPlaceholder"}>
            {hasTotal ? currencyFormat(total, "GBP") : "—"}
          </Text>
        )}
        <Label>
          <Text bold as="span">
            {label}
          </Text>
          {hint && (
            <TooltipComponent content={hint} contentMaxWidth={280}>
              <HintTrigger type="button" aria-label={`About ${label.toLowerCase()}`}>
                <UIcon icon="infoCircle" size={14} color="sysTextSecondary" />
              </HintTrigger>
            </TooltipComponent>
          )}
        </Label>
      </Headline>
      <Detail
        isLoading={isLoading}
        isError={isError}
        emptyText={emptyText}
        prefix={!hasTotal && emptyTotalText ? emptyTotalText : undefined}
        count={count}
        latestLabel={latestLabel}
        latestDate={latestDate}
      />
    </Figure>
  );
}

function Detail({
  isLoading,
  isError,
  emptyText,
  prefix,
  count,
  latestLabel,
  latestDate,
}: {
  isLoading: boolean;
  isError: boolean;
  emptyText?: string;
  prefix?: string;
  count: string;
  latestLabel: string;
  latestDate: string | null | undefined;
}) {
  if (isLoading) {
    return <SkeletonText width="200px" fontSize={14} shimmer />;
  }
  if (isError) {
    return <Text color="sysTextSecondary">Couldn't load this figure</Text>;
  }
  if (emptyText) {
    return <Text color="sysTextSecondary">{emptyText}</Text>;
  }

  const parts = [prefix, count].filter(Boolean).join(" · ");
  return (
    <Text color="sysTextSecondary">
      {parts}
      {latestDate && (
        <>
          {" · "}
          {latestLabel} {/* Relative reads faster; the exact date is a hover or focus away. */}
          <TooltipComponent content={formatDate(DateTime.fromISO(latestDate).toJSDate())}>
            <RelativeDate dateTime={latestDate} tabIndex={0}>
              {toHumanRelativeDateFromNow(latestDate)}
            </RelativeDate>
          </TooltipComponent>
        </>
      )}
    </Text>
  );
}

/** "Nothing recorded" reads as "ever", which is wrong once the page is looking at a period. */
function emptyMessage(nothing: string, dateFrom: string | undefined) {
  return dateFrom ? `${nothing} in this period` : `${nothing} recorded`;
}

// Four columns on a wide screen with the figures from the left, two on a tablet, one on a phone.
const Figures = styled.div({
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  columnGap: 48,
  rowGap: 16,
  [`@media (max-width: ${LAPTOP_SM_MIN_PX - 1}px)`]: {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
  [`@media (max-width: ${TABLET_MIN_PX - 1}px)`]: {
    gridTemplateColumns: "minmax(0, 1fr)",
  },
});

const Figure = styled.section({
  display: "flex",
  flexDirection: "column",
  gap: 8,
});

const Headline = styled.div({
  display: "flex",
  alignItems: "baseline",
  flexWrap: "wrap",
  columnGap: 8,
});

const Label = styled.span({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
});

const HintTrigger = styled.button(({ theme }) => ({
  display: "inline-flex",
  padding: 0,
  border: "none",
  background: "none",
  cursor: "help",
  alignSelf: "center",
  "&:focus-visible": {
    outline: `2px solid ${theme.colors.sysPrimaryDefault}`,
    outlineOffset: 2,
    borderRadius: 2,
  },
}));

const RelativeDate = styled.time(({ theme }) => ({
  textDecoration: "underline dotted",
  textUnderlineOffset: 3,
  cursor: "help",
  "&:focus-visible": {
    outline: `2px solid ${theme.colors.sysPrimaryDefault}`,
    outlineOffset: 2,
    borderRadius: 2,
  },
}));
